import OlMap from 'ol/Map';
import OpusLayer from 'lib/classes/opuslayer';
import parse from 'html-react-parser';

import { useState, useEffect } from 'react';
import { getOpusLayerByCode } from 'lib/utilities/map';
import { updateURLParameter, parseURLs } from 'lib/utilities/misc';
import { fetchRequest } from 'lib/utilities/network';
import { assetURL } from 'lib/utilities/asset';
import { getLocaleString } from 'lib/localization/vendor';

import 'styles/css/SearchResultPopup.scss';

const urlAttemptFields:string[] = [
	'urls', 'url', 'URL', 'URLS', 'URL(s)', 'URL(S)', 'url(s)'
]

export default function SearchResult({title, olMap, result}:any) {
	const fields:any = result.customFields ?? result; // Support for new AC encoded data field
	const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
	const urlsParsed:any[] = parseURLs(fields[getURLsField()]);
	
	const [ocDocItems, setOcDocItems] = useState<Map<string, any>>(new Map<string, any>());
	
	// Initialize
	useEffect(() => {
		loadOCDocumentURLs();
	}, [result]);
	
	function getURLsField():string {
		for(let name of urlAttemptFields) {
			if(fields[name])
				return name;
		}
		return null;
	}
	
	/* Load in the documents */
	const loadOCDocumentURLs = () => {
		
		if(!urlsParsed)
			return;
		
		urlsParsed.forEach(url => {
			const urlFinal:string = url.url;
			
			if(urlFinal.includes('/readdoc') && urlFinal.includes('oc2.uk')) {
				
				const urlRequest:string = `https://m.opusmap.co.uk/opus4ac/opusconsult.php?url=${encodeURIComponent(url.url)}`;
				
				fetchRequest(urlRequest, {})
				.then(res => res.text())
				.then(body => {
					
					const ocDocItemsNew:Map<string, any> = new Map<string, any>();
					
					const docId:number = Number(urlFinal.split('/de/')[1]);
					const docuData = body.split(`id="d${docId}"></a>`)[1].split('</div>')[0];
					const summary:string = docuData.split('<p>')[1].split('</p>')[0];
					
					const canComment:boolean = docuData.includes('user-make-comment');
					const canViewComments:boolean = docuData.includes('user-view-comment');
					
					const onClickDocument = () => {
						window.open(urlFinal, '_blank');
						//_ui.openIFrame(urlFinal, {title: getLocaleString('document')});
					}
					const onClickComment = () => {
						window.open(`https://demo.oc2.uk/repwizard/${docId}`, '_blank');
						//_ui.openIFrame(`https://demo.oc2.uk/repwizard/${docId}`, {title: getLocaleString('comment')});
					}
					const onClickViewComment = () => {
						window.open(`https://demo.oc2.uk/document/reps/${docId}`, '_blank');
						//_ui.openIFrame(`https://demo.oc2.uk/document/reps/${docId}`, {title: getLocaleString('viewcomments')});
					}
					
					const drawDocButton = () => (
						<div className="oc-doc-result__user-button clickable" onClick={onClickDocument}>
							<img src={assetURL('assets/images/oc_viewdoc.png')} />
						</div>
					)
					const drawCommentButton = () => (
						<div className="oc-doc-result__user-button clickable" onClick={onClickViewComment}>
							<img src={assetURL('assets/images/oc_comment.png')} />
						</div>
					)
					const drawViewCommentsButton = () => (
						<div className="oc-doc-result__user-button clickable" onClick={onClickComment}>
							<img src={assetURL('assets/images/oc_search.png')} />
						</div>
					)
					
					const docDraw:any = (
						<div className="oc-doc-result" key={Math.floor(Math.random() * 1000000)}>
							
							<div className="oc-doc-result__user-buttons">
								{drawDocButton()}
								{canComment && drawCommentButton()}
								{canViewComments && drawViewCommentsButton()}
							</div>
							
							<div className="oc-doc-result__external">
								<p className="ext-url">
									<a href={urlFinal} target='_blank'>
										{url.label} 
										<img src={assetURL('assets/images/external.png')} className='ql-result__icon' />
									</a> 
								</p>
								<p>
									{summary}
								</p>
							</div>
						</div>
					)
					
					ocDocItemsNew.set(urlFinal, docDraw);
					
					setOcDocItems(ocDocItemsNew);
				});
				
			}
		});
	}
	
	const drawResults = () => {
		/* Result rows */
		const resultRows:any[] = [];
		
		/* Image URL */
		let url:string = result.image;
		
		url = updateURLParameter(url, 'width', '120');
		url = updateURLParameter(url, 'height', '60');
		
		const drawResultRow = (title:string, value:any) => {
			return (
				<div className='ql-result__data-set' key={resultRows.length}>
					<div className='ql-result__sub'>
						{title}
					</div>
					<div className='ql-result__data-content'>
						{value}
					</div>
				</div>
			)
		}
		const drawNoDataLabel = () => {
			return (
				<div className='gray-label'>
					{getLocaleString('nodata')}
				</div>
			)
		}
		/* Draw URLs Method */
		const drawURLs = (urls:any[]) => {
			const rows:any[] = [];
			
			urls.forEach(url => {
				const label:string = url.label;
				const urlFinal:string = url.url;
				
				// For documents only
				const ocDocDraw:any = ocDocItems.get(urlFinal);
				
				if(urlFinal.includes('google.com/maps/') && urlFinal.includes('map_action=pano')) {
					
					const showStreetView = () => {
						const urlParams:URLSearchParams = new URLSearchParams(urlFinal);
						const viewpoint:string = urlParams.get('viewpoint');
						const lat:number = Number(viewpoint.split(',')[0]);
						const lng:number = Number(viewpoint.split(',')[1]);
						
						_ui.openGoogleMapViewAt(lng, lat, urlFinal);
					}
					
					rows.push(
						<p key={urls.indexOf(url)}>
							<button onClick={() => showStreetView()}>
								{label} 
							</button> 
						</p>
					);
				}else
				if(urlFinal.includes('google.com/maps/') && urlFinal.includes('basemap=satellite')) {
					
					const showSatelliteView = () => {
						const urlParams:URLSearchParams = new URLSearchParams(urlFinal);
						const viewpoint:string = urlParams.get('center');
						const lat:number = Number(viewpoint.split(',')[0]);
						const lng:number = Number(viewpoint.split(',')[1]);
						
						_ui.openGoogleMapViewAt(lng, lat, urlFinal, {mapType: 'satellite', mapMode: 'view'});
					}
					
					rows.push(
						<p key={urls.indexOf(url)}>
							<button onClick={() => showSatelliteView()}>
								{label} 
							</button> 
						</p>
					);
				}else
				if(urlFinal.includes('/readdoc') && urlFinal.includes('oc2.uk') && ocDocDraw) {
					
					rows.push(ocDocDraw);
					
				}else{
					rows.push(
						<p className="ext-url" key={urls.indexOf(url)}>
							<a href={urlFinal} target='_blank'>
								{label} 
								<img src={assetURL('assets/images/external.png')} className='ql-result__icon' />
							</a> 
						</p>
					);
				}
			});
			
			return rows;
		}
		
		fields.description && resultRows.push(
			drawResultRow(getLocaleString('description'), parse(fields.description))
		);
		fields.policies && resultRows.push(
			drawResultRow(getLocaleString('policies'), parse(fields.policies))
		);
		urlsParsed?.length > 0 && resultRows.push(
			drawResultRow(getLocaleString('urls'), drawURLs(urlsParsed))
		);
		
		if(result.customFields) {
			// Draw custom fields one by one
			for(let columnName in result.customFields) {
				const value:any = result.customFields[columnName];
				
				if(!value || urlAttemptFields.includes(columnName))
					continue;
				
				resultRows.push(
					drawResultRow(columnName, value)
				);
			}
		}
		
		return resultRows.length > 0 ? resultRows : drawNoDataLabel();
	}
	
	return (
		<div className='ql-result'>
			{drawResults()}
		</div>
	)
}